import { Injectable } from '@angular/core';
import { GetResult, Preferences } from '@capacitor/preferences';
import { BooleanStorageKeys, StorageService, StorageType } from './storage.service';

@Injectable({
    providedIn: 'root',
})
export class MobileStorageService implements StorageService {
    async setString(key: string, value: string): Promise<void> {
        await Preferences.set({ key, value });
    }

    async getString(key: string): Promise<GetResult> {
        const result = await Preferences.get({ key });
        return { value: result.value ?? null };
    }

    /**
     * Sets a boolean value in the storage using the specified key.
     *
     * @param {BooleanStorageKeys} key - The key associated with the value to be stored.
     * @param {boolean} value - The boolean value to store.
     * @return {Promise<void>} A promise that resolves when the operation is complete.
     */
    async setBoolean(key: BooleanStorageKeys, value: boolean): Promise<void> {
        await Preferences.set({ key: key, value: value.toString() });
    }

    /**
     * Retrieves a boolean value associated with the specified key from storage.
     * If no value is found, the provided default value is returned.
     *
     * @param {BooleanStorageKeys} key - The key for which the boolean value is to be retrieved.
     * @param {boolean} [defaultValue] - The default value to return if no value is found for the key.
     * @return {Promise<boolean>} A promise that resolves to the boolean value retrieved from storage,
     *                             or the default value if the key does not exist.
     */
    async getBoolean(key: BooleanStorageKeys, defaultValue?: boolean): Promise<boolean> {
        const result = await Preferences.get({ key });

        if (result.value === null) {
            return defaultValue ?? false;
        }

        if (JSON.parse(result.value) === true) {
            return true;
        }

        return defaultValue ?? false;
    }

    async setObject<T>(key: string, value: T): Promise<void> {
        await Preferences.set({ key, value: JSON.stringify(value) });
    }

    async getObject<T>(key: string): Promise<T | null> {
        const ret = await Preferences.get({ key });

        if (ret.value) {
            return JSON.parse(ret.value ?? '{}');
        }

        return null;
    }

    async removeItem(key: string): Promise<void> {
        await Preferences.remove({ key });
    }

    async clear(): Promise<void> {
        await Preferences.clear();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setStorageType(type: StorageType): void {
        // No-op for mobile storage as it uses Capacitor Preferences which does not differentiate between local and session storage.
    }
}
